import styled from "styled-components";
import { useEffect, useState } from "react";
import { X, RefreshCw, Check, AlertTriangle } from "lucide-react";

import { theme } from "./theme";
import { useT } from "./i18n";

/* Reads a Xendit payout back and shows the reply verbatim. The drawer only ever displayed a mapped
 * status word, so when a payout misbehaved the real reason lived nowhere an operator could reach.
 *
 * Every fetch is kept, not just the last one — "it said X an hour ago and Y now" is the shape of the
 * question this answers, and a single latest-value would lose it.
 *
 * 1500 because it is opened from INSIDE the booking drawer, whose panel sits at 1400. Below that the
 * drawer covers half the card. Same reasoning as VoidRefundDialog.
 */
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.6);
`;

const Card = styled.div`
  width: 100%;
  max-width: 640px;
  max-height: 92vh;
  overflow-y: auto;
  background: #ffffff;
  border-radius: 14px;
  padding: 1.25rem 1.25rem 1rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
`;

const Title = styled.h3`
  align-items: center;
  color: ${theme.ink};
  display: flex;
  font-size: 1.1rem;
  font-weight: 800;
  gap: 0.45rem;
  justify-content: space-between;
  margin: 0 0 0.6rem;
`;

const Intro = styled.p`
  color: ${theme.ink700};
  font-size: 0.85rem;
  line-height: 1.5;
  margin: 0 0 0.85rem;
`;

const Button = styled.button`
  align-items: center;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  font-weight: 700;
  gap: 0.35rem;
  padding: 0.55rem 1rem;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const Primary = styled(Button)`
  background: ${theme.gold};
  border: 0;
  color: ${theme.ink};
`;

const Cancel = styled(Button)`
  background: #ffffff;
  border: 1px solid ${theme.border};
  color: ${theme.ink700};
`;

const IconClose = styled.button`
  background: transparent;
  border: 0;
  color: ${theme.muted};
  cursor: pointer;
  line-height: 1;
  padding: 0.2rem;
`;

const Bar = styled.div`
  align-items: center;
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.9rem;
`;

const SectionLabel = styled.div`
  color: ${theme.muted};
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  margin-bottom: 0.4rem;
  text-transform: uppercase;
`;

const Empty = styled.div`
  border: 1px dashed ${theme.border};
  border-radius: 10px;
  color: #5e6278;
  font-size: 0.85rem;
  padding: 0.85rem;
  text-align: center;
`;

const Row = styled.div`
  border: 1px solid ${theme.border};
  border-radius: 10px;
  margin-bottom: 0.5rem;
  overflow: hidden;
`;

const RowHead = styled.div`
  align-items: center;
  background: #fbfaf7;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 0.6rem;
  padding: 0.5rem 0.7rem;
`;

const Chip = styled.span`
  align-items: center;
  background: ${(props) => (props.$ok ? "#e7f6ec" : "#fdecea")};
  border-radius: 999px;
  color: ${(props) => (props.$ok ? "#0f7a39" : "#b42318")};
  display: inline-flex;
  font-size: 0.72rem;
  font-weight: 800;
  gap: 0.25rem;
  padding: 0.15rem 0.5rem;
`;

const When = styled.span`
  color: ${theme.ink};
  font-size: 0.85rem;
  font-weight: 700;
`;

const Meta = styled.span`
  color: #4a4f63;
  font-size: 0.78rem;
`;

const ErrorText = styled.div`
  color: #b42318;
  font-size: 0.82rem;
  font-weight: 700;
  padding: 0.4rem 0.7rem 0;
`;

const Toggle = styled.button`
  background: transparent;
  border: 0;
  color: #2f4a70;
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 0.35rem 0.7rem 0.5rem;
`;

const Payload = styled.pre`
  background: #f7f7f9;
  border-top: 1px solid ${theme.border};
  color: #2f3345;
  font-size: 0.74rem;
  line-height: 1.45;
  margin: 0;
  max-height: 280px;
  overflow: auto;
  padding: 0.6rem 0.7rem;
  white-space: pre-wrap;
  word-break: break-word;
`;

const ErrorBanner = styled.div`
  align-items: flex-start;
  background: #fdecea;
  border: 1px solid #f5c6c2;
  border-radius: 8px;
  color: #b42318;
  display: flex;
  font-size: 0.84rem;
  font-weight: 700;
  gap: 0.4rem;
  line-height: 1.45;
  margin-bottom: 0.85rem;
  padding: 0.55rem 0.7rem;
`;

const Note = styled.div`
  color: #4a4f63;
  font-size: 0.78rem;
  line-height: 1.45;
  margin-top: 0.6rem;
`;

const formatWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("id-ID");
};

// Ticks once a second so the button re-enables on its own. The server enforces the same window from
// the same table, so this is a courtesy, never the guard.
const useCountdown = (untilIso) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!untilIso) return undefined;
    const deadline = new Date(untilIso).getTime();
    // Nothing to count down to — and no reason to re-render this dialog once a second for as long as
    // an operator leaves it open.
    if (Number.isNaN(deadline) || deadline <= Date.now()) return undefined;

    const id = setInterval(() => {
      setNow(Date.now());
      if (Date.now() >= deadline) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [untilIso]);

  if (!untilIso) return 0;
  const remaining = Math.ceil((new Date(untilIso).getTime() - now) / 1000);
  return remaining > 0 ? remaining : 0;
};

const PayoutFetchDialog = (props) => {
  const { t } = useT();
  const [openId, setOpenId] = useState(null);
  const info = props.info || {};
  const fetches = info.fetches || [];
  const remaining = useCountdown(info.cooldown_until);
  const blocked = props.busy || remaining > 0;

  return (
    <Backdrop onClick={props.onClose}>
      <Card onClick={(event) => event.stopPropagation()}>
        <Title>
          {t("payout_fetch_title")}
          <IconClose
            type="button"
            onClick={props.onClose}
            aria-label={t("close")}
          >
            <X size={18} />
          </IconClose>
        </Title>

        <Intro>{t("payout_fetch_intro")}</Intro>

        <Bar>
          <Primary type="button" disabled={blocked} onClick={props.onFetch}>
            <RefreshCw size={14} />
            {remaining > 0
              ? t("payout_fetch_wait").replace("%{seconds}", remaining)
              : t("payout_fetch_button")}
          </Primary>
          <Cancel type="button" onClick={props.onClose}>
            {t("close")}
          </Cancel>
        </Bar>

        {props.error ? (
          <ErrorBanner>
            <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{props.error}</span>
          </ErrorBanner>
        ) : null}

        <SectionLabel>{t("payout_fetch_history")}</SectionLabel>
        {fetches.length === 0 ? (
          <Empty>{t("payout_fetch_empty")}</Empty>
        ) : (
          fetches.map((fetch) => (
            <Row key={fetch.id}>
              <RowHead>
                <Chip $ok={fetch.success}>
                  {fetch.success ? (
                    <Check size={11} />
                  ) : (
                    <AlertTriangle size={11} />
                  )}
                  {fetch.success
                    ? t("payout_fetch_ok")
                    : t("payout_fetch_failed")}
                </Chip>
                <When>{formatWhen(fetch.at)}</When>
                {fetch.by ? (
                  <Meta>
                    {t("payout_fetch_by")} {fetch.by}
                  </Meta>
                ) : null}
                {fetch.http_status ? (
                  <Meta>HTTP {fetch.http_status}</Meta>
                ) : null}
                {fetch.payout_status ? (
                  <Meta>{fetch.payout_status}</Meta>
                ) : null}
              </RowHead>
              {fetch.error_message ? (
                <ErrorText>{fetch.error_message}</ErrorText>
              ) : null}
              <Toggle
                type="button"
                onClick={() => setOpenId(openId === fetch.id ? null : fetch.id)}
              >
                {openId === fetch.id
                  ? t("payout_fetch_hide_payload")
                  : t("payout_fetch_show_payload")}
              </Toggle>
              {openId === fetch.id ? (
                <Payload>
                  {JSON.stringify(fetch.payload || {}, null, 2)}
                </Payload>
              ) : null}
            </Row>
          ))
        )}

        <Note>{t("payout_fetch_masked_note")}</Note>
      </Card>
    </Backdrop>
  );
};

export default PayoutFetchDialog;
